<template>
  <action-dispatcher
    action="lessons/getAll"
    :parameters="$route.params.id"
    class="view-lesson"
  >
    <div v-if="quiz" class="container-fluid px-lg-5 order-lg-1">
      <header v-if="module" class="d-flex mb-3">
        <button
          class="btn btn-back text-decoration-none p-0 m-0"
          @click="goBack"
        >
          <i class="icon-chevron-left mr-2"></i> {{ module.name }}
        </button>
      </header>

      <div class="row">
        <div class="col-lg-8">
          <quiz-info
            :quiz="quiz"
            @update="dispatchQuizzes"
            :isBlocked="isBlocked"
          />
          <div class="pt-3"></div>
          <content-description
            is-quiz
            hide-finish-button
            :content="quiz"
            :finishedLesson="finishedQuiz"
            :loadingFinish="loadingFinish"
            :nextContent="nextLesson"
            :previousContent="previousLesson"
          />
        </div>
        <div class="col-lg-4">
          <b-tabs justified>
            <b-tab title="Conteúdo do módulo" active class="lesson-list-title">
              <tab-lesson-line-list :items="items" :current="quiz" />
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </action-dispatcher>
</template>

<script>
import ContentDescription from "@/components/lesson/ContentDescription";
import TabLessonLineList from "@/components/lesson/TabLessonLineList";
import QuizInfo from "@/components/lesson/quiz/QuizInfo";
import { removeTimeZone } from "@/functions/helpers";
import { mapState } from "vuex";

export default {
  components: {
    ContentDescription,
    TabLessonLineList,
    QuizInfo
  },
  data() {
    return {
      loadingFinish: false
    };
  },
  computed: {
    ...mapState("modules", ["module", "modules"]),
    ...mapState("lessons", ["lessons"]),
    ...mapState("quiz", ["quizes"]),
    ...mapState(["setup"]),
    items() {
      return [...this.lessons, ...this.quizes].sort((a, b) => a.sort - b.sort);
    },
    quiz() {
      if (!this.quizes.length) {
        return null;
      }

      const data = this.quizes.find(
        quiz => quiz.code == this.$route.params.quiz
      );

      return data;
    },
    nextLesson() {
      const index = this.items.findIndex(
        i => i.code == this.$route.params.quiz
      );

      //if there's a next content in the current module/submodule
      if (this.items[index + 1])
        return {
          ...this.items[index + 1],
          module: {
            id: this.$route.params.id
          }
        };

      const inSubmodule = !this.modules.find(
        module => module.id == this.quiz.module
      );

      // if the current lesson is inside a submodule
      if (inSubmodule) {
        const parent_module = this.modules.find(module =>
          module.sub_modules.find(
            sub_module => sub_module.id === this.quiz.module
          )
        );

        const sub_module_index = parent_module.sub_modules.findIndex(
          submodule => submodule.id == this.quiz.module
        );

        // if there's a next submodule with content
        if (
          parent_module.sub_modules[sub_module_index + 1] &&
          parent_module.sub_modules[sub_module_index + 1].items.length
        ) {
          return {
            ...parent_module.sub_modules[sub_module_index + 1].items[0],
            module: {
              id: parent_module.sub_modules[sub_module_index + 1].id
            }
          };
        }
      }

      const module_index = inSubmodule
        ? this.modules.findIndex(module =>
            module.sub_modules.find(
              sub_module => sub_module.id === this.quiz.module
            )
          )
        : this.modules.findIndex(module => module.id == this.quiz.module);

      //if there's a submodule with content in the current module
      if (
        !inSubmodule &&
        this.modules[module_index].sub_modules.length &&
        this.modules[module_index].sub_modules[0].items.length
      ) {
        return {
          ...this.modules[module_index].sub_modules[0].items[0],
          module: {
            id: this.modules[module_index].sub_modules[0].id
          }
        };
      }

      const nextModule = this.modules[module_index + 1];

      //if there's a next module with content
      if (nextModule && nextModule.items.length) {
        const moduleFirstLesson = nextModule.lessons[0];
        const submoduleFirstLesson = nextModule.sub_modules[0].lessons[0];

        //if there's a lesson in the next module (outside a submodule)
        if (moduleFirstLesson) {
          return {
            ...moduleFirstLesson,
            module: {
              id: nextModule.id
            }
          };
        }

        //if there's a submodule with lesson in the next module
        if (submoduleFirstLesson) {
          return {
            ...submoduleFirstLesson,
            module: {
              id: nextModule.sub_modules[0].id
            }
          };
        }
      }

      return null;
    },
    previousLesson() {
      const index = this.items.findIndex(
        i => i.code == this.$route.params.quiz
      );

      if (this.items[index - 1])
        return {
          ...this.items[index - 1],
          module: {
            id: this.$route.params.id
          }
        };

      const module_index = this.modules.findIndex(
        m => m.id == this.items[index].module
      );

      if (
        this.modules[module_index - 1] &&
        this.modules[module_index - 1].items.length
      )
        return {
          ...this.modules[module_index - 1].items[
            this.modules[module_index - 1].items.length - 1
          ],
          module: this.modules[module_index - 1]
        };

      return null;
    },
    finishedQuiz() {
      return !!(
        this.quiz &&
        this.quiz.progress &&
        this.quiz.progress.finished_at
      );
    },
    isBlocked() {
      return this.quiz.is_blocked || !this.isReleased;
    },
    isReleased() {
      if (!this.$user.course) return true; // is producer

      const today = new Date();
      const created_at = new Date(this.$user.course.date_joined);
      const release_date = new Date(
        created_at.setDate(created_at.getDate() + this.quiz.days_to_release)
      );

      return this.quiz.release_at
        ? new Date(removeTimeZone(this.quiz.release_at)).getTime() <
            new Date().getTime()
        : today < release_date
        ? false
        : true;
    }
  },
  mounted() {
    this.$store.dispatch("modules/getAll").then();
    this.dispatchQuizzes();
    this.dispatchModule();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "module", params: { id: this.module.id } });
    },
    dispatchModule() {
      this.$store.dispatch("modules/getDetails", this.$route.params.id).then();
    },
    dispatchQuizzes() {
      this.$store
        .dispatch("quiz/getAll", {
          module_id: this.$route.params.id,
          params: { limit: 1000, status: "published" }
        })
        .then();
    }
  }
};
</script>

<style lang="scss" scoped>
.view-lesson {
  min-height: calc(100vh - 56px);
  padding-top: 72px;

  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }

  iframe {
    height: 500px;
  }
  .tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  ::v-deep .tab-content,
  .tab-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
}
</style>
